import { tinaField, useTina } from "tinacms/dist/react";
import ButtonPrimary from "../ui-elements/ButtonPrimary";
import content from "../../../content/pages/matrix-drops.json";
import { PageDocument } from "../../../tina/__generated__/types";
import { PageSeo } from "../../navigation/PageSeo";
import "./MatrixDropsProtocol.css";

const initialData = { page: content };
const variables = { relativePath: "matrix-drops.json" };

export const MatrixDrops = () => {
  const { data } = useTina({ query: PageDocument, variables, data: initialData });
  const { hero, idea, library, coldFusion, examples, complex, computer, life, guidance, disclaimer } = data.page;

  return (
    <main className="matrix-page">
      <PageSeo pathname="/matrix-drops" seo={data.page.seo} />
      <section className="matrix-hero page-hero-dots">
        <div className="matrix-hero-copy">
          <p className="matrix-eyebrow" data-tina-field={tinaField(hero, "eyebrow")}>
            {hero.eyebrow}
          </p>
          <h1 data-tina-field={tinaField(hero, "title")}>
            {hero.title} <span>{hero.titleHighlight}</span>
          </h1>
          <p className="matrix-intro" data-tina-field={tinaField(hero, "intro")}>
            {hero.intro}
          </p>
          <div className="matrix-actions">
            <ButtonPrimary url={hero.ctaHref ?? "/booking"} label={hero.ctaLabel ?? ""} size="md" />
            <span className="matrix-text-link" data-tina-field={tinaField(hero, "secondaryLabel")}>
              {hero.secondaryLabel}
            </span>
          </div>
        </div>
        <div className="matrix-hero-art">
          <img
            src={hero.imageSrc ?? undefined}
            alt={hero.imageAlt ?? ""}
            data-tina-field={tinaField(hero, "imageSrc")}
          />
          <span className="matrix-hero-badge" data-tina-field={tinaField(hero, "badge")}>
            {hero.badge}
          </span>
        </div>
      </section>

      <section className="matrix-section matrix-split-section">
        <div>
          <p className="matrix-eyebrow" data-tina-field={tinaField(idea, "eyebrow")}>
            {idea.eyebrow}
          </p>
          <h2 data-tina-field={tinaField(idea, "title")}>{idea.title}</h2>
        </div>
        <div className="matrix-copy">
          {(idea.paragraphs ?? []).map((paragraph, index) => (
            <p key={index} data-tina-field={tinaField(idea, "paragraphs", index)}>
              {paragraph}
            </p>
          ))}
        </div>
      </section>

      <section className="matrix-section matrix-feature-section">
        <div className="matrix-feature-image">
          <img
            src={library.imageSrc ?? undefined}
            alt={library.imageAlt ?? ""}
            loading="lazy"
            data-tina-field={tinaField(library, "imageSrc")}
          />
        </div>
        <div className="matrix-feature-copy">
          <p className="matrix-eyebrow" data-tina-field={tinaField(library, "eyebrow")}>
            {library.eyebrow}
          </p>
          <h2 data-tina-field={tinaField(library, "title")}>{library.title}</h2>
          <p data-tina-field={tinaField(library, "text")}>{library.text}</p>
          <span className="matrix-button-link" data-tina-field={tinaField(library, "linkLabel")}>
            {library.linkLabel}
          </span>
        </div>
      </section>

      <section className="matrix-section matrix-dark-section">
        <div className="matrix-section-heading">
          <p className="matrix-eyebrow" data-tina-field={tinaField(coldFusion, "eyebrow")}>
            {coldFusion.eyebrow}
          </p>
          <h2 data-tina-field={tinaField(coldFusion, "title")}>{coldFusion.title}</h2>
          <p className="matrix-section-intro" data-tina-field={tinaField(coldFusion, "intro")}>
            {coldFusion.intro}
          </p>
        </div>
        <div className="matrix-callout-grid">
          {(coldFusion.callouts ?? []).map((callout, index) => (
            <article key={index} data-tina-field={tinaField(callout)}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{callout.title}</h3>
              <p>{callout.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="matrix-section">
        <div className="matrix-section-heading">
          <p className="matrix-eyebrow" data-tina-field={tinaField(examples, "eyebrow")}>
            {examples.eyebrow}
          </p>
          <h2 data-tina-field={tinaField(examples, "title")}>{examples.title}</h2>
          <p className="matrix-section-intro" data-tina-field={tinaField(examples, "intro")}>
            {examples.intro}
          </p>
        </div>
        <div className="matrix-table-grid">
          {(examples.tables ?? []).map((table, index) => (
            <div className="matrix-table-card" key={index} data-tina-field={tinaField(table)}>
              <h3>{table.title}</h3>
              <table>
                <thead>
                  <tr>
                    <th>Focus</th>
                    <th>{table.codeHeading}</th>
                  </tr>
                </thead>
                <tbody>
                  {(table.rows ?? []).map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <td>{row?.name}</td>
                      <td>
                        <span className="matrix-code">{row?.code}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
        <p className="matrix-table-note" data-tina-field={tinaField(examples, "note")}>
          {examples.note}
        </p>
      </section>

      <section className="matrix-section matrix-common-section">
        <div className="matrix-section-heading">
          <p className="matrix-eyebrow" data-tina-field={tinaField(complex, "eyebrow")}>
            {complex.eyebrow}
          </p>
          <h2 data-tina-field={tinaField(complex, "title")}>{complex.title}</h2>
        </div>
        <div className="matrix-common-grid">
          {(complex.items ?? []).map((item, index) => (
            <article className="matrix-common-card" key={index} data-tina-field={tinaField(item)}>
              <span>{item.code}</span>
              <strong>{item.name}</strong>
              {item.detail && <small>{item.detail}</small>}
            </article>
          ))}
        </div>
        <span className="matrix-button-link" data-tina-field={tinaField(complex, "linkLabel")}>
          {complex.linkLabel}
        </span>
      </section>

      <section className="matrix-section matrix-computer-section">
        <div className="matrix-computer-copy">
          <p className="matrix-eyebrow" data-tina-field={tinaField(computer, "eyebrow")}>
            {computer.eyebrow}
          </p>
          <h2 data-tina-field={tinaField(computer, "title")}>{computer.title}</h2>
          <p data-tina-field={tinaField(computer, "text")}>{computer.text}</p>
          <div className="matrix-actions">
            <span className="matrix-button-link" data-tina-field={tinaField(computer, "primaryLabel")}>
              {computer.primaryLabel}
            </span>
            <span className="matrix-text-link" data-tina-field={tinaField(computer, "secondaryLabel")}>
              {computer.secondaryLabel}
            </span>
          </div>
        </div>
        <div className="matrix-computer-list">
          <p data-tina-field={tinaField(computer, "topicsTitle")}>{computer.topicsTitle}</p>
          <ul>
            {(computer.topics ?? []).map((topic, index) => (
              <li key={index} data-tina-field={tinaField(computer, "topics", index)}>
                {topic}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="matrix-section matrix-life-section">
        {(life ?? []).map((card, index) => (
          <div className="matrix-life-card" key={index} data-tina-field={tinaField(card)}>
            <h3>{card.title}</h3>
            <p>{card.text}</p>
            <span className="matrix-life-link">{card.linkLabel}</span>
          </div>
        ))}
      </section>

      <section className="matrix-section matrix-guidance-section">
        <p className="matrix-eyebrow" data-tina-field={tinaField(guidance, "eyebrow")}>
          {guidance.eyebrow}
        </p>
        <h2 data-tina-field={tinaField(guidance, "title")}>{guidance.title}</h2>
        <div className="matrix-guidance-grid">
          {(guidance.steps ?? []).map((step, index) => (
            <div key={index} data-tina-field={tinaField(guidance, "steps", index)}>
              <strong>{String(index + 1).padStart(2, "0")}</strong>
              <p>{step}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="matrix-disclaimer">
        <h2 data-tina-field={tinaField(disclaimer, "title")}>{disclaimer.title}</h2>
        <p data-tina-field={tinaField(disclaimer, "text")}>{disclaimer.text}</p>
      </section>
    </main>
  );
};

export default MatrixDrops;
